/// <amd-module name="Feed/Legacy/Components/Settings/AddAadInternalSourcePanelSection" />
import * as React from "react";

import { ITextField, TextField } from "OfficeFabric/TextField";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";

import { IVssPageContext } from "VSS/Platform/Context";

import * as Utils_String from "VSS/Core/Util/String";
import { Uri } from "VSS/Platform/Util/Url";

import { UpstreamSource, UpstreamSourceType } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { FeedSettingsActionCreator } from "Feed/Legacy/Actions/FeedSettingsActionCreator";
import { AddUpstreamProtocolSelection } from "Feed/Legacy/Components/Settings/AddUpstreamProtocolSelection";
import { UpstreamConstants } from "Feed/Legacy/Components/Settings/CommonTypes";
import { IInternalUpstreamSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import * as ServiceEndpoint from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ExtendedUpstreamSource } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IAddAadInternalSourcePanelSectionProps {
    pageContext: IVssPageContext;
    selectedUpstreamSourcesHandler: (sources: UpstreamSource[]) => void;
    upstreamSourceNameInvalidMessage?: string;
    internalUpstreamSettings: IInternalUpstreamSettingsState;
    serviceEndpoints: ServiceEndpoint.ServiceEndpoint[];
    isServiceEndpointUpstreamsFeatureEnabled: boolean;
    projectId?: string;
}

export interface IAddAadInternalSourcePanelSectionState {
    currentLocator: string;
    upstreamName: string;
    isLocatorValid: boolean;
    selectedProtocols: string[];
    useServiceConnectionChecked: boolean;
    selectedServiceConnection: ServiceEndpoint.ServiceEndpoint;
}

export class AddAadInternalSourcePanelSection extends React.Component<
    IAddAadInternalSourcePanelSectionProps,
    IAddAadInternalSourcePanelSectionState
> {
    private _isUpstreamNameDirty: boolean = false;
    private _locatorTextField: ITextField = null;
    private _isUpackCrossCollectionUpstreamAvailable: boolean;

    constructor(props: IAddAadInternalSourcePanelSectionProps) {
        super(props);

        this.state = {
            currentLocator: null,
            upstreamName: "",
            isLocatorValid: false,
            selectedProtocols: [],
            useServiceConnectionChecked: false,
            selectedServiceConnection: null
        };

        this._isUpackCrossCollectionUpstreamAvailable = isFeatureFlagEnabled(this.props.pageContext,
            "Packaging.Feed.UPackCrossCollectionUpstreams", false);  
    }

    public render(): JSX.Element {
        return (
            <div className="rhythm-vertical-16">
                <div>
                    <TextField
                        label={Utils_String.format(
                            PackageResources.AddUpstream_LocatorTextFieldLabel,
                            PackageResources.AzureArtifacts
                        )}
                        placeholder={this.formatWithProjectScopedLocator(
                            PackageResources.AddUpstream_FeedLocatorTextField_Placeholder
                        )}
                        required={true}
                        onGetErrorMessage={this._getErrorMessage}
                        validateOnLoad={false}
                        onChanged={this._onLocatorChanged}
                        ref={(element: ITextField) => (this._locatorTextField = element)}
                    />
                </div>
                <div>
                    <AddUpstreamProtocolSelection
                        selectedProtocolsChangedHandler={this.selectedProtocolsChangedHandler}
                        internalUpstreamSettings={this.props.internalUpstreamSettings}
                        isUPackSameCollectionUpstreamAvailable={false}
                        isUPackCrossCollectionUpstreamAvailable={this._isUpackCrossCollectionUpstreamAvailable}
                    />
                </div>
                <div>
                    <TextField
                        label={PackageResources.AddUpstream_UpstreamNameTextFieldLabel}
                        onChanged={this._onUpstreamNameChanged}
                        value={this.state.upstreamName}
                        disabled={!this.state.isLocatorValid || this.state.currentLocator == null}
                        required={true}
                        maxLength={UpstreamConstants.MaxUpstreamNameLength}
                        errorMessage={this.props.upstreamSourceNameInvalidMessage}
                    />
                </div>
                {this.props.isServiceEndpointUpstreamsFeatureEnabled ?
                    <div>
                        <Checkbox
                            label={PackageResources.AddUpstreamPanel_UseServiceConnectionLabel}
                            checked={this.state.useServiceConnectionChecked}
                            onChange={(event, checked) => this._onCheckboxChange(checked)}
                        />
                    </div> : <div /> }
                {this.props.isServiceEndpointUpstreamsFeatureEnabled && this.state.useServiceConnectionChecked ?
                    <div>
                        <Dropdown
                            placeHolder={this._getServiceConnectionsSelectorPlaceholderText()}
                            label={PackageResources.AddUpstream_ServiceConnectionsSelectorLabel}
                            options={this._getServiceConnectionOptions()}
                            disabled={this.props.serviceEndpoints?.length === 0}
                            onChanged={this._onSelectedServiceConnectionChanged}
                            required={true}
                            selectedKey={!!this.state.selectedServiceConnection
                                ? this.state.selectedServiceConnection.id
                                : null }
                        />
                    </div>: <div /> }
            </div>
        );
    }

    private _onCheckboxChange(checked: boolean) {
        this.setState({useServiceConnectionChecked: checked}, this._updateUpstreamSources);
    }

    private _getServiceConnectionsSelectorPlaceholderText(): string {
        if(this.props.serviceEndpoints == null || this.props.serviceEndpoints.length === 0) {
            return PackageResources.AddUpstream_ServiceConnectionsSelectorPlaceholder_NoConnectionsAvailable;
        }

        return PackageResources.AddUpstream_ServiceConnectionsSelectorPlaceholder_Default;
    }

    private _getServiceConnectionOptions(): IDropdownOption[] {
        if(this.props.isServiceEndpointUpstreamsFeatureEnabled
            && this.state.useServiceConnectionChecked
            && this.props.serviceEndpoints != null) {
            return this.props.serviceEndpoints.map((connection: ServiceEndpoint.ServiceEndpoint) => ({
                key: connection.id,
                text: connection.name,
                data: connection
            }));
        }
        return [];
    }

    private _onSelectedServiceConnectionChanged = (item: IDropdownOption): void => {
        if(item.data == null) {
            return;
        }

        const selectedConnection = item.data as ServiceEndpoint.ServiceEndpoint;
        this.setState({ selectedServiceConnection: selectedConnection }, this._updateUpstreamSources);
    }

    public componentDidMount(): void {
        this.setFocus();
    }

    private _onLocatorChanged = (newValue: string): void => {
        // Setting location is valid to false to wait until validation kicks in
        this.setState(
            {
                isLocatorValid: false
            },
            this._updateUpstreamSources
        );
    };

    private _setErrorStateOnLocatorChanged(): void {
        const name: string = this._isUpstreamNameDirty ? this.state.upstreamName : "";
        this.setState(
            {
                upstreamName: name,
                isLocatorValid: false
            },
            this._updateUpstreamSources
        );
    }

    private _onUpstreamNameChanged = (newValue: string): void => {
        FeedSettingsActionCreator.changeUpstreamSourceName.invoke(newValue);

        this._isUpstreamNameDirty = true;

        this.setState({ upstreamName: newValue }, this._updateUpstreamSources);
    };

    private _getUpstreamName(host: string, project: string, feed: string, view: string): string {
        if (this._isUpstreamNameDirty) {
            return this.state.upstreamName;
        }

        if (!host || !feed || !view) {
            return "";
        }

        const currentLength = host.length + project.length + feed.length + view.length;
        const maxLength = UpstreamConstants.MaxUpstreamNameLength - 3;
        if (currentLength > maxLength) {
            const sectionLength = maxLength / (3 + (project ? 1 : 0));
            host = host.substring(0, sectionLength);
            project = project.substring(0, sectionLength);
            feed = feed.substring(0, sectionLength);
            view = view.substring(0, sectionLength);
        }

        return project ? `${host}-${project}-${feed}@${view}` : `${host}-${feed}@${view}`;
    }

    private _updateUpstreamSources(): void {
        this.props.selectedUpstreamSourcesHandler(this._getUpstreamSources());
    }

    private _getUpstreamSources(): ExtendedUpstreamSource[] {
        if (
            this.state.currentLocator != null &&
            this.state.upstreamName != null &&
            this.state.upstreamName.length > 0 &&
            this.state.isLocatorValid &&
            ((this.state.useServiceConnectionChecked && this.state.selectedServiceConnection != null)
                || !this.state.useServiceConnectionChecked)
        ) {
            const upstreamSources: ExtendedUpstreamSource[] = [];
            for (const protocol of this.state.selectedProtocols) {
                upstreamSources.push({
                    id: null,
                    name: this.state.upstreamName,
                    location: this.state.currentLocator,
                    protocol,
                    upstreamSourceType: UpstreamSourceType.Internal,
                    deletedDate: null,
                    internalUpstreamCollectionId: null,
                    internalUpstreamFeedId: null,
                    internalUpstreamViewId: null,
                    serviceEndpointId: this.state.useServiceConnectionChecked 
                        ? this.state.selectedServiceConnection.id : null,
                    useServiceEndpoint: this.state.useServiceConnectionChecked,
                    serviceEndpointProjectId: this.props.projectId
                } as ExtendedUpstreamSource);
            }

            return upstreamSources.length > 0 ? upstreamSources : null;
        }

        return null;
    }

    private selectedProtocolsChangedHandler = (protocols: string[]): void => {
        this.setState({ selectedProtocols: protocols }, this._updateUpstreamSources);
    };

    private _getErrorMessage = (newValue: string): string => {
        if (newValue.length === 0) {
            this.setState(
                {
                    isLocatorValid: false,
                    upstreamName: this._getUpstreamName(null, null, null, null),
                    currentLocator: null
                },
                this._updateUpstreamSources
            );
            return "";
        }

        const uri = new Uri(newValue);

        // Validate the locator is a URI with a scheme (i.e. "thisIsAScheme://"), but
        // the service validates the scheme is actually "azure-feed".
        if (!uri || uri.scheme.length === 0) {
            this._setErrorStateOnLocatorChanged();
            return this.formatWithProjectScopedLocator(
                PackageResources.AddUpstream_LocatorTextField_Error_FormatCouldNotBeParsed
            );
        }

        const path = uri.path;

        if (!path || path.length === 0) {
            this._setErrorStateOnLocatorChanged();
            return this.formatWithProjectScopedLocator(
                PackageResources.AddUpstream_LocatorTextField_Error_NoFeedProvided
            );
        }

        if (path.indexOf("@") < 0) {
            this._setErrorStateOnLocatorChanged();
            return this.formatWithProjectScopedLocator(
                PackageResources.AddUpstream_LocatorTextField_Error_NoViewProvided
            );
        }

        // regex with capture either
        // project/feed@view/error  -- match[1] == "project", match[2] == "feed", match[3] == "view", match[4] == "error"
        // feed@view/error          -- match[1] == "feed", match[2] == null, match[3] == "view", match[4] == "error"
        const matches = path.match(/\/*([^\/]*)\/?([^\/]*)@([^\/]+)\/*(.*)/i);

        if (!matches) {
            this._setErrorStateOnLocatorChanged();
            return this.formatWithProjectScopedLocator(
                PackageResources.AddUpstream_LocatorTextField_Error_FormatCouldNotBeParsed
            );
        }

        // The regex above would capture in the fourth group the section between stars: azure-feed://foo/bar@bar/**this/would/be/captured/by/the/fourth/group/**
        if (matches[4].length > 0) {
            this._setErrorStateOnLocatorChanged();
            return this.formatWithProjectScopedLocator(
                PackageResources.AddUpstream_LocatorTextField_Error_LocatorContainsSegmentAfterView
            );
        }

        let project = "";
        let feed = matches[1];
        const view = matches[3];
        if (matches[2]) {
            project = matches[1];
            feed = matches[2];
        }

        // Validation error if a project was supplied but feature flag is not enabled
        if (project.length > 0 && !this.props.internalUpstreamSettings.isProjectScopedUpstreamsEnabled) {
            this._setErrorStateOnLocatorChanged();
            return this.formatWithProjectScopedLocator(
                PackageResources.AddUpstream_LocatorTextField_Error_FormatCouldNotBeParsed
            );
        }

        this.setState(
            {
                isLocatorValid: true,
                upstreamName: this._getUpstreamName(uri.host, project, feed, view),
                currentLocator: newValue
            },
            this._updateUpstreamSources
        );

        return "";
    };

    private setFocus(): void {
        if (this._locatorTextField) {
            this._locatorTextField.focus();
        }
    }

    private formatWithProjectScopedLocator(resourceString: string): string {
        return Utils_String.format(
            resourceString,
            this.props.internalUpstreamSettings.isProjectScopedUpstreamsEnabled
                ? PackageResources.AddUpstream_FeedLocatorTextField_OrProjectScopedLocator
                : ""
        );
    }
}
